import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';
import type {
  BatchOperationHelpers,
  BatchOperationItemResult,
  BatchOperationPhase,
  BatchOperationRunOptions,
  BatchOperationState,
} from './types';

const initialState: BatchOperationState = {
  phase: 'idle',
  summary: null,
};

function isRunningPhase(phase: BatchOperationPhase): boolean {
  return phase === 'queued' || phase === 'running' || phase === 'polling' || phase === 'refreshing';
}

function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.name === 'BatchOperationTimeoutError';
}

function createTimeoutError(message: string): Error {
  const error = new Error(message);
  error.name = 'BatchOperationTimeoutError';
  return error;
}

function summarizeItems(items: BatchOperationItemResult[]) {
  return {
    total: items.length,
    succeeded: items.filter((item) => item.status === 'success').length,
    failed: items.filter((item) => item.status === 'failed').length,
    warnings: items.filter((item) => item.status === 'warning').length,
    skipped: items.filter((item) => item.status === 'skipped').length,
    items,
  };
}

export function useBatchOperation() {
  const [state, setState] = useState<BatchOperationState>(initialState);
  const mountedRef = useRef(true);
  const activeRunIdRef = useRef<number | null>(null);
  const nextRunIdRef = useRef(0);
  const activePromiseRef = useRef<Promise<unknown> | null>(null);
  const timersRef = useRef<Set<number>>(new Set());

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    timersRef.current.clear();
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      activeRunIdRef.current = null;
      clearTimers();
    };
  }, [clearTimers]);

  const safeSetState = useCallback((runId: number, updater: SetStateAction<BatchOperationState>) => {
    if (!mountedRef.current || activeRunIdRef.current !== runId) {
      return;
    }
    setState(updater);
  }, []);

  const waitForRun = useCallback(
    (runId: number, ms: number) =>
      new Promise<void>((resolve, reject) => {
        if (!mountedRef.current || activeRunIdRef.current !== runId) {
          reject(createTimeoutError('Batch operation was canceled.'));
          return;
        }

        const timerId = window.setTimeout(() => {
          timersRef.current.delete(timerId);
          if (!mountedRef.current || activeRunIdRef.current !== runId) {
            reject(createTimeoutError('Batch operation was canceled.'));
            return;
          }
          resolve();
        }, ms);
        timersRef.current.add(timerId);
      }),
    []
  );

  const run = useCallback(
    async <TResult,>(options: BatchOperationRunOptions<TResult>): Promise<TResult> => {
      if (activePromiseRef.current && !options.allowConcurrent) {
        throw new Error('A batch operation is already running.');
      }

      clearTimers();
      const runId = nextRunIdRef.current + 1;
      nextRunIdRef.current = runId;
      activeRunIdRef.current = runId;
      const startedAt = new Date().toISOString();

      safeSetState(runId, {
        phase: 'running',
        title: options.title,
        message: options.message,
        totalCount: options.totalCount,
        startedAt,
        completedAt: undefined,
        attempt: undefined,
        error: undefined,
        summary: null,
      });

      const helpers: BatchOperationHelpers = {
        updatePhase: (phase, message) => {
          safeSetState(runId, (current) => ({
            ...current,
            phase,
            message: message ?? current.message,
          }));
        },
        updateSummary: (summary) => {
          safeSetState(runId, (current) => ({
            ...current,
            summary,
          }));
        },
        wait: (ms) => waitForRun(runId, ms),
        pollUntil: async (pollOptions) => {
          const intervalMs = pollOptions.intervalMs ?? 500;
          const initialDelayMs = pollOptions.initialDelayMs ?? intervalMs;
          const maxAttempts = pollOptions.maxAttempts ?? 60;

          for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            const delayMs = attempt === 1 ? initialDelayMs : intervalMs;
            if (delayMs > 0) {
              await waitForRun(runId, delayMs);
            }
            safeSetState(runId, (current) => ({
              ...current,
              phase: current.phase === 'queued' ? 'polling' : current.phase,
              attempt,
            }));

            const result = await pollOptions.poll(attempt);
            pollOptions.onAttempt?.(result, attempt);

            if (!pollOptions.isPending(result)) {
              return { result, attempts: attempt };
            }
          }

          throw createTimeoutError(pollOptions.timeoutMessage || 'Batch operation timed out.');
        },
        runSequential: async (sequentialOptions) => {
          const items: BatchOperationItemResult[] = [];

          for (let index = 0; index < sequentialOptions.items.length; index += 1) {
            safeSetState(runId, (current) => ({
              ...current,
              phase: 'running',
              attempt: index + 1,
            }));
            const item = sequentialOptions.items[index];
            const result = await sequentialOptions.runItem(item, index);
            const itemResult = sequentialOptions.toItemResult(result, item, index);
            items.push(itemResult);
            helpers.updateSummary(summarizeItems(items));
          }

          return summarizeItems(items);
        },
      };

      const operationPromise = (async () => {
        try {
          const work = options.run(helpers);
          const result =
            options.timeoutMs && options.timeoutMs > 0
              ? await Promise.race([
                  work,
                  waitForRun(runId, options.timeoutMs).then(() => {
                    throw createTimeoutError(options.timeoutMessage || 'Batch operation timed out.');
                  }),
                ])
              : await work;

          const summary = options.toSummary ? options.toSummary(result) : null;
          const completedAt = new Date().toISOString();
          safeSetState(runId, (current) => {
            const nextSummary = summary ?? current.summary;
            const failed = nextSummary?.failed ?? 0;
            const warnings = nextSummary?.warnings ?? 0;
            return {
              ...current,
              phase: failed > 0 || warnings > 0 ? 'warning' : 'completed',
              completedAt,
              summary: nextSummary,
            };
          });
          return result;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Batch operation failed.';
          const completedAt = new Date().toISOString();
          safeSetState(runId, (current) => ({
            ...current,
            phase: isTimeoutError(error) ? 'timeout' : 'failed',
            completedAt,
            error: message,
            summary:
              current.summary ??
              {
                total: options.totalCount ?? 0,
                succeeded: 0,
                failed: options.totalCount ?? 1,
                message,
                startedAt,
                completedAt,
              },
          }));
          throw error;
        } finally {
          if (activeRunIdRef.current === runId) {
            activeRunIdRef.current = null;
            activePromiseRef.current = null;
            clearTimers();
          }
        }
      })();

      activePromiseRef.current = operationPromise;
      return operationPromise;
    },
    [clearTimers, safeSetState, waitForRun]
  );

  const reset = useCallback(() => {
    clearTimers();
    activeRunIdRef.current = null;
    activePromiseRef.current = null;
    setState(initialState);
  }, [clearTimers]);

  return useMemo(
    () => ({
      state,
      run,
      reset,
      isRunning: isRunningPhase(state.phase),
    }),
    [reset, run, state]
  );
}
